import { ReferentRows } from '@/components/admin/versioned/referent-rows';
import {
  ReferentUpdatePanel,
  referentVersionCount,
  selectedReferents,
} from '@/components/admin/versioned/referent-update-panel';
import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import type { VersionedWording } from '@/lib/versioned-labels';
import type { ReferentPlan, ReferentRef, UsageLine, VersionTag } from '@matter_extractor/shared';
import { versionNoteSchema } from '@matter_extractor/shared';
import { type ReactNode, useId, useState } from 'react';

/**
 * « Enregistrer une nouvelle version » (refonte modulaire §4; maquettes
 * `EnregistrerVersion` / `EnregistrerVersionProd`).
 *
 * It names nothing but the entity the caller hands it, so the same box serves
 * every versioned object: the two sentences quote the name, the referent rows
 * read the usage, and the sentence under them - who plays the object - is the
 * object's own, from its `VersionedWording` (issue #155).
 *
 * ## One gesture: the passage to production has its own door (vitrine.md V15, issue #192)
 *
 * The box used to carry a checkbox - « Passer aussi cette version en production » - and a button
 * that said which of the two gestures it was doing. It carries neither any more: saving writes a
 * version and moves « latest dev », and nothing else. Passing a version to the prod is the gesture
 * « Passer en production » (the header, a line of the history), which opens the box of the
 * cascade and its verdict - one door, so that nothing reaches the prod without the prod having
 * judged it. `SaveVersionRequest.alsoProduction` stays on the wire, and the editor always says
 * false.
 *
 * ## The referents that pin an older version (vitrine.md §3.3, H22)
 *
 * An object that other objects cite hands the box its PLAN
 * (`GET {id}/referents`): who pins a version older than the one being written,
 * who follows `latest` by itself. The panel `ReferentUpdatePanel` lists them, a
 * checkbox each, checked by default, and ticking creates their new versions in
 * the same request - « Enregistrer la v7 et 2 nouvelles versions ». That panel
 * REPLACES the list « Ce que la v7 change en développement » it was drawn
 * for: a referent that pins a version does not see the v7, so the question that
 * list answered - who will play the new version - is the panel's own now. An
 * object with nothing citing it says so: « Utilisé nulle part encore ».
 *
 * ## What the object may REFUSE from inside the box (issue #49)
 *
 * `renderBlockers` is the one opening this box has, and it is optional. §7.1
 * makes the save of a protocol refusable on the content itself - a step without
 * a turn cap, an objective without a criterion - and makes the tick refusable
 * separately, on the criteria it cites. Both are states the layer cannot read,
 * and both have to be said HERE rather than after the click: a box that let a
 * click through to a 409 nobody expected is exactly what the pure rule set of
 * `protocol/problems.ts` exists to avoid.
 *
 * It is called with the state of the CHECKBOX, because the two refusals differ
 * by it, and its `blocked` drives `confirmDisabled` - which this shell already
 * had. The criterion and the persona pass nothing and render what they always
 * did.
 */

export interface SaveDialogProps {
  wording: VersionedWording;
  /** The entity's name, for the sentence under the title. */
  name: string;
  /** The number this save will write. */
  nextNumber: number;
  usage: readonly UsageLine[];
  /**
   * Who plays each pointer of the entity, when the object knows it better than
   * `usage` (review of !462, issue #148): the lines of a tag ARE its referents,
   * each with its own pointers that play it, drawn as given. Absent, the two
   * lists are `usage` filtered by tag - the layer's rule.
   */
  referentsByTag?: Readonly<Record<VersionTag, readonly UsageLine[]>>;
  saving: boolean;
  error: string | null;
  /**
   * The referents of the object, for an object that other objects cite: the plan
   * once it is read, `undefined` while it is, and absent for an object that
   * nothing cites or while the box has no entity to ask about.
   */
  referents?: { readonly plan: ReferentPlan | undefined } | undefined;
  /**
   * What the object refuses about the content being saved: the content to render under the
   * description, and whether it closes the button. `null` when nothing is refused - and absent
   * for an object that refuses nothing here at all. Called with `false`: the box no longer
   * carries the tick that made the production refusals part of a save (V15).
   */
  renderBlockers?: (alsoProduction: boolean) => { content: ReactNode; blocked: boolean } | null;
  onConfirm: (input: { note: string | null; updateReferents: ReferentRef[] }) => void;
  onCancel: () => void;
}

export function SaveDialog({
  wording,
  name,
  nextNumber,
  usage,
  referentsByTag,
  saving,
  error,
  referents,
  renderBlockers,
  onConfirm,
  onCancel,
}: SaveDialogProps) {
  const noteId = useId();
  const [note, setNote] = useState('');
  /** The referent lines the operator UNCHECKED: everything starts ticked (§3.3). */
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set());
  const plan = referents?.plan;
  const extra = plan === undefined ? 0 : referentVersionCount(plan, unchecked);
  const blockers = renderBlockers?.(false) ?? null;
  // Who plays the object is the object's to say (`VersionedWording.developmentHint`, #155).
  const developmentPlayed =
    referentsByTag === undefined
      ? usage.some((referent) => referent.tags.includes('development'))
      : referentsByTag.development.length > 0;
  const developmentHint = developmentPlayed ? wording.developmentHint(nextNumber) : null;
  const byReference = referentsByTag === undefined ? {} : { byReference: true };

  return (
    <VersionedDialog
      data-testid={`${wording.entity}-boite-enregistrer`}
      title="Enregistrer une nouvelle version"
      description={`« ${name} » passe en v${nextNumber}. Le pointeur latest dev la suivra.`}
      confirmLabel={
        extra > 0
          ? `Enregistrer la v${nextNumber} et ${extra} nouvelle${extra > 1 ? 's' : ''} version${extra > 1 ? 's' : ''}`
          : `Enregistrer la v${nextNumber}`
      }
      busy={saving}
      error={error}
      confirmDisabled={blockers?.blocked ?? false}
      onConfirm={() =>
        onConfirm({
          note: note.trim() === '' ? null : note.trim(),
          updateReferents: plan === undefined ? [] : selectedReferents(plan, unchecked),
        })
      }
      onCancel={onCancel}
    >
      {/* FIRST, under the description: what would be refused is read before the
          note somebody is about to write, not after the referent lists.
          INERT while saving (second review of !474): its « Ouvrir » and
          « Ouvrir le protocole » are doors out of the box, and none may close
          it over a version in flight - greyed, so it is seen as well. */}
      {blockers === null ? null : (
        <div inert={saving} className={saving ? 'opacity-60' : undefined}>
          {blockers.content}
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor={noteId} className="block font-semibold text-gray-800 text-sm">
          Note de version <span className="font-normal text-gray-600">(facultative)</span>
        </label>
        <input
          id={noteId}
          data-testid={`${wording.entity}-note-version`}
          value={note}
          /* One line of the history and not a changelog - `versionNoteSchema`
             caps it, and the cap is read from the schema so the field cannot
             accept what the server would refuse. */
          maxLength={versionNoteSchema.maxLength ?? undefined}
          placeholder="Ce que cette version change"
          onChange={(event) => setNote(event.currentTarget.value)}
          className="h-11 w-full rounded-md border-2 border-gray-300 bg-white px-3 text-base focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        />
      </div>

      {referents === undefined ? (
        <VersionedDialogSection
          title={`Ce que la v${nextNumber} change en latest dev`}
          hint={developmentHint}
        >
          {/* « Utilisé nulle part encore » SURVIVES here, where the card and the
              editor panel dropped it (§4): this box answers « qui jouera cette
              version », and an empty list is an answer to that - not the zero
              count a fresh card would have carried. */}
          <ReferentRows
            entity={wording.entity}
            usage={referentsByTag?.development ?? usage}
            tag="development"
            emptyMessage="Utilisé nulle part encore : rien d’autre ne change."
            {...byReference}
          />
        </VersionedDialogSection>
      ) : (
        <VersionedDialogSection title={`Ce que la v${nextNumber} change`} hint={null}>
          {plan === undefined ? (
            <p
              data-testid={`${wording.entity}-referents-lecture`}
              className="text-gray-500 text-sm italic"
            >
              Lecture des référents…
            </p>
          ) : plan.pinning.length + plan.following.length === 0 ? (
            <p
              data-testid={`${wording.entity}-referents-vide`}
              className="text-gray-500 text-sm italic"
            >
              Utilisé nulle part encore : rien d’autre ne change.
            </p>
          ) : (
            <ReferentUpdatePanel
              entity={wording.entity}
              name={name}
              plan={plan}
              unchecked={unchecked}
              disabled={saving}
              onUnchecked={setUnchecked}
            />
          )}
        </VersionedDialogSection>
      )}
    </VersionedDialog>
  );
}
